import React, { useEffect, useState } from 'react';
import { Form } from 'react-bootstrap';
import { reduxForm } from 'redux-form';
import { Field } from 'redux-form';
import {
  Box,
  Card,
  Stack,
  Typography,
  SubmitButton,
  Button,
  Divider,
} from '@fortress-technology-solutions/fortress-component-library/Atoms';
import ErrorMessage from '../../../components/ErrorMessage';
import {
  renderTextField,
  renderPasswordField,
  renderCheckboxField,
} from '../../../utils/redux-form-helper';
import messages from '../messages';
import validate from './validate';
import { useShouldShowAzureSSO } from '../../../config/azureAuthConfig';

export const LoginFormView = ({
  intl,
  handleSubmit,
  handleForget,
  remember,
  username,
  showErrorMessage,
  errorMessage,
  history,
  valid,
  toggleRememberMe,
  onMicrosoftSignIn,
  isSsoSubmitting,
}: any) => {
  const [isSubmitting, setIsSubmitting] = useState(false);
  const showMicrosoftSSO = useShouldShowAzureSSO();

  useEffect(() => {
    if (showErrorMessage && isSubmitting) {
      setIsSubmitting(false);
    }
  }, [showErrorMessage, isSubmitting]);

  return (
    <Form
      onSubmit={(login) => {
        setIsSubmitting(true);
        handleSubmit(login);
      }}
    >
      {showErrorMessage && <ErrorMessage message={errorMessage} />}

      {remember && username ? (
        <div className="login-details text-center">
          <Typography paragraph={true} variant={'titleMedium'}>
            {intl.formatMessage(messages.welcomeBack)}
          </Typography>
          <Typography paragraph={true} variant={'titleLarge'}>
            {username}
          </Typography>
          <Typography
            variant={'bodyMedium'}
            sx={{ color: 'primary.main', cursor: 'pointer' }}
          >
            <a id={'handle-forget'} onClick={() => handleForget()}>
              {' '}
              {intl.formatMessage(messages.changeUser)}
            </a>
          </Typography>
        </div>
      ) : (
        <Field
          name="username"
          showLabel={true}
          classes="blue-label pull-left"
          label={intl.formatMessage(messages.username)}
          component={renderTextField}
          bsSize="lg"
          forceShowError={false}
        />
      )}
      <span className="pull-right">
        <a id="forgotPw" onClick={() => history.push('/forgot-password')}>
          {intl.formatMessage(messages.forgotPassword)}
        </a>
      </span>
      <br />
      <Field
        name="password"
        classes="blue-label pull-left"
        label={intl.formatMessage(messages.password)}
        component={renderPasswordField}
        bsSize="lg"
      />
      <Field
        name="rememberMe"
        component={renderCheckboxField}
        onChange={toggleRememberMe}
        label={intl.formatMessage(messages.rememberUser)}
      />
      <Stack justifyContent={'center'} alignItems={'center'}>
        <SubmitButton
          variant={'primary'}
          type="submit"
          isSubmitting={isSubmitting}
          sx={{ height: 40, width: 160 }}
          disabled={!valid || isSubmitting}
        >
          {intl.formatMessage(messages.signIn)}
        </SubmitButton>
      </Stack>

      {showMicrosoftSSO && (
        <>
          <Stack direction="row" alignItems="center" sx={{ my: 3 }}>
            <Box sx={{ flex: 1, height: '1px', bgcolor: 'divider' }} />
            <Divider
              orientation="horizontal"
              textAlign="center"
              variant="fullWidth"
              sx={{ mx: 2 }}
            >
              <Typography variant="bodyMedium">
                {intl.formatMessage(messages.orDivider)}
              </Typography>
            </Divider>

            <Box sx={{ flex: 1, height: '1px', bgcolor: 'divider' }} />
          </Stack>

          <Stack justifyContent={'center'} alignItems={'center'}>
            <Button
              variant="outlined"
              onClick={onMicrosoftSignIn}
              disabled={isSsoSubmitting}
              sx={{
                height: 40,
                width: '100%',
                maxWidth: 300,
                textTransform: 'none',
                borderColor: 'divider',
                color: 'text.primary',
                '&:hover': {
                  borderColor: 'primary.main',
                  bgcolor: 'action.hover',
                },
                '&.Mui-disabled': {
                  opacity: 0.6,
                },
              }}
            >
              {!isSsoSubmitting && (
                <Box
                  component="img"
                  //eslint-disable-next-line max-len
                  src="data:image/svg+xml;base64,PHN2ZyB4bWxucz0iaHR0cDovL3d3dy53My5vcmcvMjAwMC9zdmciIHdpZHRoPSIyMSIgaGVpZ2h0PSIyMSI+PGcgZmlsbD0ibm9uZSI+PHBhdGggZmlsbD0iI0YyNTAyMiIgZD0iTTAgMGgxMHYxMEgweiIvPjxwYXRoIGZpbGw9IiM3RkJBMDAiIGQ9Ik0xMSAwaDEwdjEwSDExeiIvPjxwYXRoIGZpbGw9IiMwMDdGQjYiIGQ9Ik0wIDExaDEwdjEwSDB6Ii8+PHBhdGggZmlsbD0iI0ZGQjkwMCIgZD0iTTExIDExaDEwdjEwSDExeiIvPjwvZz48L3N2Zz4="
                  alt="Microsoft logo"
                  sx={{ width: 20, height: 20, mr: 1.5 }}
                />
              )}
              {isSsoSubmitting
                ? intl.formatMessage(messages.signingIn)
                : intl.formatMessage(messages.signInWithMicrosoft)}
            </Button>
          </Stack>
        </>
      )}
    </Form>
  );
};

export const LoginForm = ({
  intl,
  handleSubmit,
  handleForget,
  rememberMe,
  username,
  showErrorMessage,
  errorMessage,
  submitting,
  history,
  valid,
  toggleRememberMe,
  environmentName,
  onMicrosoftSignIn,
  isSsoSubmitting,
}: any) => {
  return (
    <Box
      sx={{
        margin: 'auto',
        position: 'relative',
        top: {
          xs: '15dvh',
          sm: '25vh',
        },
        width: {
          xs: 'calc(100vw - 32px)',
          sm: 450,
        },
      }}
    >
      <Card
        sx={{
          paddingX: 4,
          paddingY: 4,
          borderStyle: 'unset',
          borderWidth: 'unset',
          borderBottom: '5px solid transparent',
          borderColor: {
            development: '#7734e7',
            training: '#4cbb17',
            staging: '#98beff',
            ephemeral: '#4cbb17',
          }[environmentName],
        }}
      >
        <Typography
          variant={'headlineMedium'}
          component={'h1'}
          className="fortress-logo-gray"
          align={'center'}
          sx={{
            height: 56,
          }}
        >
          <span style={{ visibility: 'hidden' }}>Fortress</span>
        </Typography>
        <Typography paragraph align={'center'} mb={4}>{`${environmentName
          .charAt(0)
          .toUpperCase()}${environmentName.slice(1)}`}</Typography>

        <LoginFormView
          intl={intl}
          handleSubmit={handleSubmit}
          handleForget={handleForget}
          valid={valid}
          submitting={submitting}
          showErrorMessage={showErrorMessage}
          errorMessage={errorMessage}
          remember={rememberMe}
          username={username}
          toggleRememberMe={toggleRememberMe}
          history={history}
          onMicrosoftSignIn={onMicrosoftSignIn}
          isSsoSubmitting={isSsoSubmitting}
        />
      </Card>
      <Typography
        variant={'bodySmall'}
        paragraph={true}
        align={'center'}
        mt={2}
      >
        {intl.formatMessage(messages.help)}
      </Typography>
    </Box>
  );
};
export default reduxForm({
  form: 'login',
  validate: validate,
  enableReinitialize: true,
})(LoginForm);
